import React, { useEffect, useCallback } from "react";
import { X, Users, Calendar } from "lucide-react";
import { PriorityIndicator } from "./PriorityIndicator";
import ModalProps from "../types/ModalProps";
import isEmptyHtmlField from "../utils/isEmptyFieldHtml";
import resolveUrl from "../utils/resolveUrl";
import { TextWithGlossary } from "./TextWithGlossary";
import parse from "html-react-parser";

export const Modal: React.FC<ModalProps> = ({ action, isOpen, onClose }) => {
	const handleEscape = useCallback(
		(event: KeyboardEvent) => {
			if (event.key === "Escape") {
				onClose();
			}
		},
		[onClose]
	);

	useEffect(() => {
		if (isOpen) {
			document.addEventListener("keydown", handleEscape);
			document.body.style.overflow = "hidden";
		}
		return () => {
			document.removeEventListener("keydown", handleEscape);
			document.body.style.overflow = "unset";
		};
	}, [isOpen, handleEscape]);

	if (!isOpen) return null;

	const totalEffort = action.team.reduce(
		(acc, item) => acc + item.estimated,
		0
	);

	const handleBackdropClick = (e: React.MouseEvent) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	return (
		<div
			className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 overflow-y-auto py-8 cursor-pointer listStyleDisc"
			onClick={handleBackdropClick}
			role="dialog"
			aria-modal="true"
		>
			<div
				className="bg-gray-900 rounded-xl shadow-xl w-full max-w-3xl mx-4 transform transition-all cursor-default"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="sticky top-0 z-10 bg-gray-900 rounded-t-xl border-b border-gray-800 p-6 flex justify-between items-center">
					<h2 className="text-lg font-semibold text-blue-400">
						<TextWithGlossary>{action.area.title}</TextWithGlossary>
					</h2>
					<button
						onClick={onClose}
						className="hover:bg-gray-800 p-2 rounded-lg transition-colors group"
						aria-label="Fechar"
					>
						<X
							size={24}
							className="text-gray-400 group-hover:text-white transition-colors"
						/>
					</button>
				</div>
				<div className="p-6 max-h-[calc(100vh-12rem)] overflow-y-auto">
					<div className="flex items-center gap-3 mb-6">
						<PriorityIndicator priority={action.priority} size="lg" />
						<h3 className="text-xl font-bold text-white">
							<TextWithGlossary>{action.title}</TextWithGlossary>
						</h3>
					</div>
					<div className="space-y-6">
						{isEmptyHtmlField(action.problem_html) && (
							<div>
								<h4 className="text-sm font-medium text-gray-400 mb-2">
									<TextWithGlossary>Problema</TextWithGlossary>
								</h4>
								<div className="text-gray-200">
									<TextWithGlossary>
										{parse(action.problem_html)}
									</TextWithGlossary>
								</div>
							</div>
						)}
						{isEmptyHtmlField(action.risks_html) && (
							<div>
								<h4 className="text-sm font-medium text-gray-400 mb-2">
									<TextWithGlossary>Riscos</TextWithGlossary>
								</h4>
								<div className="text-gray-200">
									<TextWithGlossary>
										{parse(action.risks_html)}
									</TextWithGlossary>
								</div>
							</div>
						)}
						{isEmptyHtmlField(action.actionPlan_html) && (
							<div>
								<h4 className="text-sm font-medium text-gray-400 mb-2">
									<TextWithGlossary>Plano de Ação</TextWithGlossary>
								</h4>
								<div className="text-gray-200">
									<TextWithGlossary>
										{parse(action.actionPlan_html)}
									</TextWithGlossary>
								</div>
							</div>
						)}
						{action.links.length > 0 && (
							<div>
								<h4 className="text-sm font-medium text-gray-400 mb-2">
									<TextWithGlossary>Links Importantes</TextWithGlossary>
								</h4>
								<ul className="space-y-2 !list-none !ml-0">
									{action.links.map((link, index) => (
										<li key={index}>
											<a
												href={resolveUrl(link.linkUrl)}
												target="_blank"
												rel="noopener noreferrer"
												className="text-blue-400 hover:text-blue-300 transition-colors"
											>
												<TextWithGlossary>{link.linkTitle}</TextWithGlossary>
											</a>
										</li>
									))}
								</ul>
							</div>
						)}
						{action.checklist.length > 0 && (
							<div>
								<h4 className="text-sm font-medium text-gray-400 mb-3">
									<TextWithGlossary>Checklist de Testes</TextWithGlossary>
								</h4>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									{action.checklist.map((category, index) => (
										<div key={index} className="bg-gray-800 rounded-lg p-4">
											<h6 className="text-blue-400 font-medium mb-2">
												<TextWithGlossary>
													{category.checklistTitle}
												</TextWithGlossary>
											</h6>
											<div className="text-gray-300 text-sm">
												<TextWithGlossary>
													{parse(category.checklistDescription_html)}
												</TextWithGlossary>
											</div>
										</div>
									))}
								</div>
							</div>
						)}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{action.team.length > 0 && (
								<div className="bg-gray-800 rounded-lg p-4">
									<div className="flex items-center gap-2 mb-3">
										<Users className="w-5 h-5 text-purple-400" />
										<h5 className="text-white font-medium">
											<TextWithGlossary>Equipes Envolvidas</TextWithGlossary>
										</h5>
									</div>
									<div className="space-y-2">
										{action.team.map((member, index) => (
											<div key={index} className="flex justify-between text-sm">
												<span className="text-gray-300">
													<TextWithGlossary>{member.role}</TextWithGlossary>
												</span>
												<span className="text-gray-400">
													{member.estimated}h
												</span>
											</div>
										))}
										<div className="border-t border-gray-700 pt-2 mt-2">
											<div className="flex justify-between text-sm font-medium">
												<span className="text-gray-300">
													<TextWithGlossary>Total</TextWithGlossary>
												</span>
												<span className="text-purple-400">{totalEffort}h</span>
											</div>
										</div>
									</div>
								</div>
							)}
							{(isEmptyHtmlField(action.acceptanceCriteria_html) ||
								action.estimatedCompletionDate) && (
								<div className="bg-gray-800 rounded-lg p-4">
									<div className="flex items-center gap-2 mb-3">
										<Calendar className="w-5 h-5 text-yellow-400" />
										<h5 className="text-white font-medium">
											<TextWithGlossary>Prazos e Critérios</TextWithGlossary>
										</h5>
									</div>
									<div className="space-y-4">
										{action.estimatedCompletionDate && (
											<div>
												<h6 className="text-sm text-gray-400 mb-1">
													<TextWithGlossary>Prazo Estimado</TextWithGlossary>
												</h6>
												<p className="text-yellow-400 font-medium">
													<TextWithGlossary>
														{action.estimatedCompletionDate}
													</TextWithGlossary>
												</p>
											</div>
										)}
										{isEmptyHtmlField(action.acceptanceCriteria_html) && (
											<div>
												<h6 className="text-sm text-gray-400 mb-1">
													<TextWithGlossary>
														Critérios de Aceitação
													</TextWithGlossary>
												</h6>
												<div className="text-gray-300 text-sm">
													<TextWithGlossary>
														{parse(action.acceptanceCriteria_html)}
													</TextWithGlossary>
												</div>
											</div>
										)}
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
